import { CheckCircle2, Loader2, TriangleAlert } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { CopyButton } from '@/components/copy-button';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api-client';
import { formatMoney } from '@/lib/format';
import type { ImportDeliveryStatus, ImportPreview, ImportResult } from '@/lib/types';

interface Props {
  accountId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Chamado após import com sucesso (refetch do detalhe). */
  onImported: () => void;
}

// Q-412: o worker do portal PROD roda 1x/min; acompanha por ~6 min.
const DELIVERY_POLL_MS = 15_000;
const DELIVERY_POLL_MAX = 24;

interface DeliveryWatch {
  prodAccountId: string;
  initialPending: number;
  status: ImportDeliveryStatus;
  phase: 'watching' | 'done' | 'timeout';
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Comando pro Lucas colar em prod. null se o id não for um UUID (nunca monta shell com outra coisa). */
function manualCommand(prodAccountId: string): string | null {
  if (!UUID_RE.test(prodAccountId)) return null;
  // -w 60: espera o cron soltar o lock em vez de sair calado com código 1.
  return (
    'sudo -u seekkey-prod /usr/bin/flock -w 60 /opt/seekkey-prod/portal/shared/psn-import-assign.lock ' +
    `/usr/bin/node /opt/seekkey-prod/portal/current/workers/psn-import-assign.js --conta ${prodAccountId.toLowerCase()}`
  );
}

function formatPricePaid(preview: ImportPreview): string {
  if (preview.price_paid === null || preview.price_paid === undefined) return '—';
  // Compra pode ser em USD/TRY/etc — usar a moeda que a API manda, não assumir BRL.
  return formatMoney(preview.price_paid, preview.currency ?? 'BRL');
}

/**
 * Q-246 Import 1-clique: dialog próprio alimentado por GET /import/preview.
 * Blockers desabilitam o confirm; o label do confirm carrega o número de
 * acessos (prevenção de erro, Nielsen #5). POST só com { confirm: true }.
 */
export function ImportConfirmDialog({ accountId, open, onOpenChange, onImported }: Props) {
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const importingRef = useRef(false); // guard anti-duplo-clique
  // Bump força re-fetch do preview após falha no POST (ver catch do onConfirm).
  const [reloadKey, setReloadKey] = useState(0);
  // Q-412: depois do import, acompanha a entrega automática dos pendentes.
  const [watch, setWatch] = useState<DeliveryWatch | null>(null);

  useEffect(() => {
    if (!open) {
      setPreview(null);
      setLoadError(null);
      setWatch(null);
      return;
    }
    let active = true;
    setLoading(true);
    setLoadError(null);
    api<{ data?: ImportPreview } | ImportPreview>(`/psn-accounts/${accountId}/import/preview`)
      .then((res) => {
        if (!active) return;
        const p = 'account' in res ? (res as ImportPreview) : ((res as { data?: ImportPreview }).data ?? null);
        if (p) setPreview(p);
        else setLoadError('Resposta inesperada do preview.');
      })
      .catch(() => {
        if (active) setLoadError('Não foi possível montar o preview do import.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [open, accountId, reloadKey]);

  // Q-412: polling do estado da entrega enquanto há pendente.
  const watching = watch?.phase === 'watching';
  useEffect(() => {
    if (!open || !watching) return;
    let active = true;
    let polls = 0;
    let timer: number | undefined;
    const tick = async () => {
      polls += 1;
      try {
        const res = await api<{ data: ImportDeliveryStatus }>(
          `/psn-accounts/${accountId}/import/delivery`,
        );
        if (!active) return;
        const st = res.data;
        if (st.pending_items === 0) {
          setWatch((w) => (w ? { ...w, status: st, phase: 'done' } : w));
          return;
        }
        setWatch((w) => (w ? { ...w, status: st } : w));
      } catch {
        /* falha isolada: tenta no próximo tick */
      }
      if (!active) return;
      if (polls >= DELIVERY_POLL_MAX) {
        setWatch((w) => (w ? { ...w, phase: 'timeout' } : w));
        return;
      }
      timer = window.setTimeout(tick, DELIVERY_POLL_MS);
    };
    timer = window.setTimeout(tick, DELIVERY_POLL_MS);
    return () => {
      active = false;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [open, watching, accountId]);

  const slots = preview?.slots ?? [];
  const blockers = preview?.blockers ?? [];
  const warnings = preview?.warnings ?? [];
  const canConfirm = !!preview && blockers.length === 0 && !loading && !importing;

  const onConfirm = async () => {
    if (!canConfirm || importingRef.current) return;
    importingRef.current = true;
    setImporting(true);
    try {
      const res = await api<{ data: ImportResult }>(`/psn-accounts/${accountId}/import`, {
        method: 'POST',
        body: { confirm: true },
      });
      onImported();
      const delivery = res?.data?.delivery ?? null;
      if (delivery && delivery.pending_items > 0 && res.data.prod_account_id) {
        // Fica aberto mostrando a entrega dos pedidos que esperavam o jogo.
        setWatch({
          prodAccountId: res.data.prod_account_id,
          initialPending: delivery.pending_items,
          status: delivery,
          phase: 'watching',
        });
        toast.success('Conta importada. Entregando os pedidos pendentes…');
      } else if (!delivery) {
        toast.warning(
          'Conta importada. Não deu para conferir os pedidos pendentes; a entrega automática roda sozinha a cada minuto.',
        );
        onOpenChange(false);
      } else {
        toast.success('Conta importada pro estoque de produção. Nenhum pedido esperando este jogo.');
        onOpenChange(false);
      }
    } catch {
      // NÃO afirmar "nada foi alterado": num timeout pós-commit, prod pode ter
      // recebido a conta. Re-busca o preview — os blockers (email_already_in_prod,
      // status_not_game_purchased) contam a verdade e travam re-import duplicado.
      toast.error('Falha ao importar. Verifique o estado antes de repetir.');
      setReloadKey((k) => k + 1);
    } finally {
      importingRef.current = false;
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !importing && onOpenChange(o)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Importar pro estoque de produção</DialogTitle>
          <DialogDescription>Isto é irreversível. Confira antes de confirmar.</DialogDescription>
        </DialogHeader>

        {watch ? (
          <DeliveryPanel watch={watch} />
        ) : loading ? (
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-9 w-full" />
            ))}
          </div>
        ) : loadError ? (
          <p className="py-4 text-sm text-muted-foreground">{loadError}</p>
        ) : preview ? (
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <ReadField label="Jogo" value={preview.game?.title ?? '—'} />
              <ReadField label="Conta" value={preview.account.email} mono />
              <ReadField
                label="Variantes"
                value={
                  slots.length > 0
                    ? `${slots.length} acessos: ${slots.map((s) => s.variant_label).join(' · ')}`
                    : '—'
                }
              />
              <ReadField
                label="Recovery"
                value={`${preview.recovery_codes.available} → ${preview.recovery_codes.will_link} vinculados`}
              />
              <ReadField label="Preço pago" value={formatPricePaid(preview)} />
              <ReadField label="Região" value={preview.account.region} />
            </div>

            {warnings.length > 0 && (
              <ul className="space-y-1 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-400">
                {warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            )}

            {blockers.length > 0 && (
              <ul className="space-y-1 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
                {blockers.map((b, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
                    {b}
                  </li>
                ))}
              </ul>
            )}

            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <TriangleAlert className="h-4 w-4 shrink-0 text-amber-400" />A conta sai da fila e
              passa a &quot;Importada&quot;.
            </p>
          </div>
        ) : null}

        <DialogFooter>
          {watch ? (
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Fechar
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={importing}>
                Cancelar
              </Button>
              <Button onClick={onConfirm} disabled={!canConfirm}>
                {importing && <Loader2 className="h-4 w-4 animate-spin" />}
                {slots.length > 0 ? `Sim, importar ${slots.length} acessos` : 'Sim, importar'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeliveryPanel({ watch }: { watch: DeliveryWatch }) {
  const { status, phase, initialPending, prodAccountId } = watch;
  // Nada se mexeu desde o import: aí sim o worker pode estar parado.
  const stalled = status.delivered_from_account === 0 && status.pending_items >= initialPending;
  const command = manualCommand(prodAccountId);
  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-3">
        <ReadField label="Esperando o jogo" value={String(initialPending)} />
        <ReadField label="Entregues por esta conta" value={String(status.delivered_from_account)} />
        <ReadField label="Ainda pendentes" value={String(status.pending_items)} />
      </div>
      {phase === 'watching' && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
          Entrega automática em andamento (roda a cada minuto). Pode fechar; ela continua sozinha.
        </p>
      )}
      {phase === 'done' && (
        <p className="flex items-center gap-2 text-sm text-emerald-400">
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          Pedidos pendentes deste jogo entregues.
        </p>
      )}
      {phase === 'timeout' && !stalled && (
        <p className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-400">
          Ainda há {status.pending_items} item(ns) pendente(s) deste jogo que esta conta não atendeu:
          variante sem acesso livre aqui, reembolso em análise ou variante indefinida no pedido. Rodar
          de novo não muda isso; precisa de estoque da variante certa ou de olhar o pedido no painel.
        </p>
      )}
      {phase === 'timeout' && stalled && (
        <div className="space-y-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-400">
          <p>
            Nada mudou em 6 minutos. Ou nenhum pendente é de uma variante que esta conta atende
            (reembolso em análise, variante indefinida, outra geração), ou a entrega automática está
            parada. Para tirar a dúvida, peça ao Lucas para rodar em produção (mostra o resultado):
          </p>
          {command ? (
            <div className="flex items-start gap-2">
              <code className="break-all rounded bg-black/30 p-2 font-mono text-xs">{command}</code>
              <CopyButton value={command} label="Comando" plainToast />
            </div>
          ) : (
            <p>Id da conta de produção inválido; confira o log do import.</p>
          )}
        </div>
      )}
    </div>
  );
}

function ReadField({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs uppercase text-muted-foreground">{label}</Label>
      <div className={mono ? 'break-all font-mono text-sm' : 'text-sm'}>{value || '—'}</div>
    </div>
  );
}
